import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calendar, Users, MessageCircle, BarChart2, SmilePlus, Meh, Frown, HelpCircle, Zap } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const StakeholderEngagement = () => {
  const [stakeholders, setStakeholders] = useState([
    { id: 1, group: "Employees", lastEngagement: "2023-05-15", nextEngagement: "2023-08-15", sentiment: "Positive", keyIssues: ["Work-life balance", "Career development"], sentimentHistory: [3, 4, 4, 5] },
    { id: 2, group: "Investors", lastEngagement: "2023-04-01", nextEngagement: "2023-07-01", sentiment: "Neutral", keyIssues: ["ESG performance", "Long-term strategy"], sentimentHistory: [3, 3, 3, 3] },
    { id: 3, group: "Local Communities", lastEngagement: "2023-03-10", nextEngagement: "2023-09-10", sentiment: "Mixed", keyIssues: ["Environmental impact", "Job creation"], sentimentHistory: [2, 3, 2, 3] },
    { id: 4, group: "Customers", lastEngagement: "2023-06-01", nextEngagement: "2023-09-01", sentiment: "Positive", keyIssues: ["Product sustainability", "Ethical sourcing"], sentimentHistory: [4, 4, 5, 5] },
  ]);

  const [newMeeting, setNewMeeting] = useState({ stakeholder: '', date: '' });
  const [showMeetingForm, setShowMeetingForm] = useState(false);

  const getSentimentInfo = (sentiment) => {
    switch(sentiment) {
      case 'Positive': 
        return { color: 'text-green-600', icon: SmilePlus };
      case 'Neutral': 
        return { color: 'text-yellow-600', icon: Meh };
      case 'Mixed': 
        return { color: 'text-orange-600', icon: HelpCircle };
      case 'Negative': 
        return { color: 'text-red-600', icon: Frown };
      default: 
        return { color: 'text-gray-600', icon: HelpCircle };
    }
  };

  const handleScheduleMeeting = () => {
    if (newMeeting.stakeholder && newMeeting.date) {
      setStakeholders(stakeholders.map(s => 
        s.id === parseInt(newMeeting.stakeholder) 
          ? {...s, nextEngagement: newMeeting.date} 
          : s
      ));
      setNewMeeting({ stakeholder: '', date: '' });
      setShowMeetingForm(false);
    }
  };

  const sentimentTrendData = [2020, 2021, 2022, 2023].map((year, index) => ({
    year,
    ...stakeholders.reduce((acc, stakeholder) => {
      acc[stakeholder.group] = stakeholder.sentimentHistory[index];
      return acc;
    }, {})
  }));

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold mb-6">Stakeholder Engagement</h1>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Stakeholder Sentiment Trend</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={sentimentTrendData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="year" />
              <YAxis domain={[0, 5]} ticks={[0, 1, 2, 3, 4, 5]} />
              <Tooltip />
              <Legend />
              {stakeholders.map((stakeholder, index) => (
                <Line 
                  key={stakeholder.id}
                  type="monotone" 
                  dataKey={stakeholder.group} 
                  stroke={`hsl(${index * 90}, 70%, 50%)`} 
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {stakeholders.map((stakeholder) => {
          const sentimentInfo = getSentimentInfo(stakeholder.sentiment);
          const SentimentIcon = sentimentInfo.icon;

          return (
            <Card key={stakeholder.id}>
              <CardHeader>
                <CardTitle className="flex justify-between items-center">
                  <span>{stakeholder.group}</span>
                  <span className={`font-semibold ${sentimentInfo.color} flex items-center`}>
                    <SentimentIcon className="mr-1 h-5 w-5" />
                    {stakeholder.sentiment}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 mb-4">
                  <p><Calendar className="inline mr-2 h-4 w-4" /> Last Engagement: {stakeholder.lastEngagement}</p>
                  <p><Calendar className="inline mr-2 h-4 w-4" /> Next Scheduled: {stakeholder.nextEngagement}</p>
                  <div><MessageCircle className="inline mr-2 h-4 w-4" /> Key Issues:
                    <ul className="list-disc list-inside pl-5">
                      {stakeholder.keyIssues.map((issue, index) => (
                        <li key={index}>{issue}</li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <Button variant="outline" size="sm"><Users className="mr-2 h-4 w-4" /> Schedule Meeting</Button>
                  <Button variant="outline" size="sm"><BarChart2 className="mr-2 h-4 w-4" /> View Insights</Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {showMeetingForm ? (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Schedule New Meeting</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex space-x-2">
              <Select 
                value={newMeeting.stakeholder} 
                onValueChange={(value) => setNewMeeting({...newMeeting, stakeholder: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Stakeholder" />
                </SelectTrigger>
                <SelectContent>
                {stakeholders.map(s => (
                    <SelectItem key={s.id} value={s.id.toString()}>{s.group}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                type="date"
                value={newMeeting.date}
                onChange={(e) => setNewMeeting({...newMeeting, date: e.target.value})}
              />
              <Button onClick={handleScheduleMeeting}>Schedule Meeting</Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Button className="mt-6" onClick={() => setShowMeetingForm(true)}>
          <Calendar className="mr-2 h-4 w-4" /> Schedule New Meeting
        </Button>
      )}

      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Zap className="mr-2 h-5 w-5 text-yellow-500" />
            AI-Generated Engagement Suggestions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="list-disc pl-5 space-y-2">
            <li>Employee sentiment has been consistently positive. Consider implementing an employee recognition program to maintain this trend.</li>
            <li>Investor sentiment has remained neutral. Prepare a detailed ESG performance report for the next engagement to address their concerns about long-term strategy.</li>
            <li>Local community sentiment is mixed. Organize a town hall meeting to discuss environmental impact concerns and showcase job creation initiatives.</li>
            <li>Customer sentiment is highly positive. Leverage this by initiating a customer referral program focused on your sustainable products.</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default StakeholderEngagement;